import { find } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ProgressButton from '@/components/common/progress-button';
import TabList from '@/components/common/tablist';
import SortContactDropdown from '@/components/contacts/contact-list/sort-contact-dropdown';
import type { Props as AppHeaderProps } from '@/components/layouts/page-layout/app-header';
import PageLayout, { type PageLayoutProps } from '@/components/layouts/page-layout/page-layout';
import ForwardSessionList from '@/components/sessions/session-list/forward-session-list';
import useForwardList, { type ForwardListItem, type ForwardListType } from '@/hooks/contacts/use-forward-list';
import { friendsQueryOptions } from '@/hooks/contacts/use-friends';
import { groupsQueryOptions } from '@/hooks/contacts/use-groups';
import type { ForwardTarget } from '@/hooks/forward/use-batch-forward';
import { tagListQueryOptions } from '@/hooks/tags/use-tags';
import useAdjustLayout from '@/hooks/use-adjust-layout';
import useUserPlatform from '@/hooks/use-user-platform';
import type { Tag } from '@/models/user/tag';
import { useConfigurationStore } from '@/stores/configuration';
import { addTranslateToast } from '@/stores/toast';
import cn from '@/utils/merge-class-names';
import { queryClient } from '@/utils/query-client';
import { generateSessionKey } from '@/utils/session';
import SearchInput from '../common/search-input';
import SessionCarousel from '../conversations/session-carousel';

type InitialValue = { userIds: number[]; groupIds: number[]; tagIds: number[] };

type Props = {
  onForward: (targets: ForwardTarget[], clearSelection: () => void) => void;
  isLoading?: boolean;
  initialValue?: InitialValue;
  tabOptions?: ForwardListType[];
  buttonLabel?: string;
} & Pick<PageLayoutProps, 'title' | 'className'> &
  Pick<AppHeaderProps, 'navigateBack'>;

export default function ForwardForm({
  onForward,
  title,
  className,
  isLoading,
  navigateBack,
  initialValue,
  tabOptions,
  buttonLabel,
}: Props) {
  const { t } = useTranslation();
  const { isDesktop } = useUserPlatform();
  const [selectedSessions, setSelectedSessions] = useState<Map<string, ForwardListItem>>(new Map());
  const { options, searchTerm, listType, setSearchTerm, setListType, sortBy, setSortBy } = useForwardList('recent');

  const selectedSessionCount = useMemo(() => {
    let count = 0;
    for (const s of selectedSessions.values()) {
      count += s.type === 'tag' ? s.userIds.length : 1;
    }
    return count;
  }, [selectedSessions]);

  useEffect(() => {
    (async () => {
      const map = await transformToSelectedMap(initialValue);
      setSelectedSessions(map);
    })();
  }, [initialValue]);

  const handleChangeSelections = (sessions: ForwardListItem[], selected: boolean) => {
    const maxForwardCount = useConfigurationStore.getState().envConfig.config?.maxForwardSessionCount ?? 100;

    setSelectedSessions((prev) => {
      const map = new Map(prev);
      let newSessionCount = selectedSessionCount;

      for (const s of sessions) {
        const additionalSessionCount = s.type === 'tag' ? s.userIds.length : 1;

        if (selected) {
          if (newSessionCount + additionalSessionCount > maxForwardCount) {
            addTranslateToast({
              type: 'warning',
              message: `conversation.forward.max-selected|count=${maxForwardCount}`,
            });
            break;
          }
          map.set(s.key, s);
          newSessionCount += additionalSessionCount;
        } else {
          map.delete(s.key);
          newSessionCount -= additionalSessionCount;
        }
      }
      return map;
    });
  };

  const handleClearSelections = () => {
    setSelectedSessions(new Map());
  };

  const handleSend = () => onForward(Array.from(selectedSessions.values()), handleClearSelections);

  useAdjustLayout({ inputTargetSelector: '#search-input' });

  return (
    <PageLayout
      className={cn('w-full group-[.modal-layout]:h-75vh group-[.modal-layout]:max-h-[800px]', className)}
      title={title}
      headerConfig={{
        navigateBack,
        className: 'desktop:px-4',
        actions: (
          <ProgressButton
            as='button'
            isLoading={isLoading}
            disabled={selectedSessions.size === 0}
            onClick={handleSend}
            className='btn btn-ghost btn-sm text-primary mode-embedded:text-inherit'
            label={buttonLabel || 'conversation.forward.send'}
          />
        ),
      }}>
      {!!selectedSessions.size && (
        <div className='flex flex-col w-full items-stretch gap-2 h-28'>
          <h5 className='mx-5 text-body2 desktop:mx-4'>
            {t('conversation.forward.selected', { count: selectedSessionCount })}
          </h5>
          <SessionCarousel
            sessions={Array.from(selectedSessions.values())}
            className='mx-5 desktop:mx-4'
            onRemove={(key) => handleChangeSelections([key], false)}
          />
        </div>
      )}

      <SearchInput
        keyword={searchTerm}
        setKeyword={setSearchTerm}
        clearKeyword={searchTerm ? () => setSearchTerm('') : undefined}
        className='mt-3 mx-5'
        icon
      />

      <TabList
        className='my-3'
        defaultActiveTab={listType}
        tabs={(
          tabOptions || (isDesktop ? ['recent', 'friends', 'groups'] : ['recent', 'friends', 'groups', 'tags'])
        ).map((item) => ({
          key: item,
          label: `conversation.forward.${item}`,
        }))}
        onClickTab={(key) => setListType(key as ForwardListType)}
      />

      <div className='flex flex-1 flex-col desktop:overflow-y-auto'>
        <ForwardSessionList
          key={listType}
          sessions={options}
          selectedSessions={selectedSessions}
          onSelectSessions={handleChangeSelections}>
          {listType === 'friends' && (
            <div className='w-full flex justify-between'>
              <h3 className='text-h3 font-bold'>
                {t(`conversation.forward.${listType}`)} ({options.length})
              </h3>
              <SortContactDropdown
                sortBy={sortBy}
                setSortBy={setSortBy}
              />
            </div>
          )}
        </ForwardSessionList>
      </div>
    </PageLayout>
  );
}

export const transformToSelectedMap = async (args: InitialValue | undefined): Promise<Map<string, ForwardListItem>> => {
  const friends = await queryClient.ensureQueryData(friendsQueryOptions);
  const groups = await queryClient.ensureQueryData(groupsQueryOptions);
  const tagMap = (await queryClient.ensureQueryData(tagListQueryOptions))?.Data?.Tags.reduce<Record<string, Tag>>(
    (acc, tag) => {
      acc[tag.Id] = { ...tag };
      return acc;
    },
    {},
  );

  if (!args || !friends || !groups || !tagMap) return new Map();
  const { userIds, groupIds, tagIds } = args;

  const map = new Map();

  groupIds.forEach((id) => {
    const group = find(groups, (g) => g.ID === id);
    if (!group) return;
    const key = generateSessionKey('group', id);
    map.set(key, {
      key: generateSessionKey('group', id),
      type: 'group',
      groupId: group.ID,
      imageURL: group.GroupImg,
      name: group.GroupName,
      userCount: group.UserCount,
    });
  });

  userIds.forEach((id) => {
    if (!friends[id]) return;
    const key = generateSessionKey('session', friends[id].ChatSessionID);
    map.set(key, {
      key: generateSessionKey('session', friends[id].ChatSessionID),
      type: 'user',
      userId: friends[id].FriendID,
      imageURL: friends[id].FriendHeadImg,
      name: friends[id].RemarkName || friends[id].FriendNickName,
      remarkName: friends[id].RemarkName,
    });
  });

  tagIds.forEach((id) => {
    if (!tagMap[id]) return;
    const key = `tag-${id}`;

    map.set(key, {
      key: key,
      type: 'tag',
      name: `${tagMap[id].TagName} (${tagMap[id].TagCount})`,
      tagId: tagMap[id].Id,
      userIds: tagMap[id].TagMember.map((member) => member.Id),
    });
  });
  return map;
};
